import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, type BadgeCardProps } from '@pandiafi/components'

import type { CoreParkingSpot } from '../../../common/validations/contractParkingSpotsSchema'
import { isNullishStr, normDate } from './parkingSpots.helpers'
import {
  spotEquipmentLabel,
  spotStatusLabel,
  spotDisplayId,
  findActiveReservation,
  findFutureReservation,
  findPreviousReservation,
  formatDate,
} from './uiSelectors'

export interface ParkingSpotDetailsProps {
  spot: CoreParkingSpot
  status?: BadgeCardProps['status']
  equipmentTextOverride?: string
  className?: string
}

const Pair: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) =>
  value ? (
    <div className="grid grid-cols-2 gap-3">
      <strong>{label}</strong>
      <div>{value}</div>
    </div>
  ) : null

export const ParkingSpotDetails: React.FC<ParkingSpotDetailsProps> = ({
  spot,
  status,
  equipmentTextOverride,
  className,
}) => {
  const { t, i18n } = useTranslation('parking')
  const lang = i18n.language

  const equipmentText =
    equipmentTextOverride ?? spotEquipmentLabel(spot, t) ?? undefined
  const statusLabel = status ? spotStatusLabel(status, t) : undefined
  const displayId = spotDisplayId(spot)

  const active = findActiveReservation(spot)
  const future = findFutureReservation(spot)
  const prev = findPreviousReservation(spot)

  const activeValue =
    active &&
    [
      formatDate(normDate(active.startDate)!, lang),
      active.endDate && normDate(active.endDate)
        ? formatDate(normDate(active.endDate)!, lang)
        : '',
    ]
      .filter(Boolean)
      .join(' - ')

  const futureValue =
    future &&
    [
      formatDate(normDate(future.startDate)!, lang),
      future.endDate && normDate(future.endDate)
        ? formatDate(normDate(future.endDate)!, lang)
        : '',
    ]
      .filter(Boolean)
      .join(' - ')

  const prevValue =
    prev &&
    [
      formatDate(normDate(prev.startDate)!, lang),
      prev.endDate && normDate(prev.endDate)
        ? formatDate(normDate(prev.endDate)!, lang)
        : '',
    ]
      .filter(Boolean)
      .join(' - ')

  return (
    <Modal.Content>
      <div className={className}>
        <div className="space-y-3">
          <Pair label={t('spot.label')} value={displayId} />
          {status && (
            <Pair label={t('spot.status')} value={statusLabel ?? status} />
          )}
          {equipmentText && (
            <Pair label={t('spot.equipment')} value={equipmentText} />
          )}
          {spot.additionalDetails && !isNullishStr(spot.additionalDetails) && (
            <Pair
              label={t('spot.details')}
              value={spot.additionalDetails.trim()}
            />
          )}
          {activeValue && status !== 'disabled' && (
            <Pair
              label={t('tooltip.reservation.current.label')}
              value={activeValue}
            />
          )}
          {futureValue && status !== 'disabled' && (
            <Pair
              label={t('tooltip.reservation.future.label')}
              value={futureValue}
            />
          )}
          {prevValue && status !== 'disabled' && (
            <Pair
              label={t('tooltip.reservation.previous.label')}
              value={prevValue}
            />
          )}
        </div>
      </div>
    </Modal.Content>
  )
}
